import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { ArrowsSplit, SpinnerGap, Warning, CheckCircle, MagnifyingGlass, ArrowLeft } from "@phosphor-icons/react";
import { splitMasterBreakdown, splitMaster, adminCollegesLiteSearch, jobsStatus } from "../lib/api";

// Split a wrongly-merged master into a new master. 3 steps: pick names → codes +
// new identity → preview & confirm. See split_ops.py for the backend contract.
export default function SplitMasterDialog({ open, onClose, sourceCode, onDone }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bd, setBd] = useState(null);            // breakdown
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState("");
  const [newPrimary, setNewPrimary] = useState("");   // which selected name is new primary

  // step 2 state
  const [dest, setDest] = useState("new");             // "new" | "existing"
  const [newCode, setNewCode] = useState("");
  const [ident, setIdent] = useState({ primary_name: "", short_name: "", state: "", college_type: "govt" });
  const [codeMoves, setCodeMoves] = useState({});      // body -> bool (move?)
  const [moveNmc, setMoveNmc] = useState(false);
  const [tgt, setTgt] = useState({ q: "", results: [], searching: false, selected: null });

  // step 3 state
  const [preview, setPreview] = useState(null);
  const [sourcePrimary, setSourcePrimary] = useState("");
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [jobProg, setJobProg] = useState({});   // key -> {status, progress} for rebuild/enrich jobs

  // ── load breakdown on open ──
  useEffect(() => {
    if (!open || !sourceCode) return;
    setStep(1); setError(""); setBd(null); setSelected(new Set()); setFilter("");
    setNewPrimary(""); setNewCode("");
    setPreview(null); setResult(null); setJobProg({}); setSourcePrimary("");
    setDest("new"); setTgt({ q: "", results: [], searching: false, selected: null });
    (async () => {
      setLoading(true);
      try {
        const { data } = await splitMasterBreakdown(sourceCode);
        setBd(data);
        setIdent((s) => ({ ...s, state: data?.master?.state || "", college_type: data?.master?.college_type || "govt" }));
        setCodeMoves(Object.fromEntries(Object.keys(data?.master?.codes || {}).map((b) => [b, false])));
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load breakdown");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, sourceCode]);

  // ── poll the split's background jobs ──
  //  • rebuild_job : the scoped derived rebuild (cutoffs/seats/search) — always
  //  • enrich_job  : Places→Website→Details→Media — new-master splits only
  // Both report progress via /admin/jobs-status (same shape).
  useEffect(() => {
    const specs = [];
    if (result?.rebuild_job) specs.push(["rebuild", result.rebuild_job]);
    if (result?.enrich_job) specs.push(["enrich", result.enrich_job]);
    if (!specs.length) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await jobsStatus(specs.map(([, id]) => id));
        if (!alive) return;
        const next = {};
        let allDone = true;
        specs.forEach(([key, id]) => {
          const status = data?.statuses?.[id] || null;
          next[key] = { status, progress: data?.progress?.[id] || null };
          if (!["completed", "failed", "cancelled"].includes(status)) allDone = false;
        });
        setJobProg(next);
        if (allDone) clearInterval(iv);
      } catch { /* transient — keep polling */ }
    };
    const iv = setInterval(tick, 2500);
    tick();
    return () => { alive = false; clearInterval(iv); };
  }, [result?.rebuild_job, result?.enrich_job]);

  const names = bd?.names || [];
  const nmcName = bd?.master?.nmc_name || "";

  // Only genuinely-secondary external codes are movable. An external code whose VALUE
  // equals the source's own internal college_code (e.g. AP_Govt → GMCM on GMCM) is the
  // master's identity mirrored under a body — that's how future rows resolve back to
  // THIS master, so it must stay unique to the source and is never offered for moving.
  const movableCodes = useMemo(
    () => Object.entries(bd?.master?.codes || {})
      .filter(([, v]) => String(v || "").toUpperCase() !== String(sourceCode || "").toUpperCase()),
    [bd, sourceCode]
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return names;
    return names.filter((r) => r.name.toLowerCase().includes(q));
  }, [names, filter]);

  const toggle = (name) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(name) ? n.delete(name) : n.add(name);
    return n;
  });
  const selectCluster = (cluster) => setSelected((prev) => {
    const n = new Set(prev);
    names.filter((r) => r.cluster === cluster).forEach((r) => n.add(r.name));
    return n;
  });
  const selectAllFiltered = () => setSelected((prev) => {
    const n = new Set(prev);
    filtered.forEach((r) => n.add(r.name));
    return n;
  });
  const clearAll = () => setSelected(new Set());

  const selCount = selected.size;
  const selRows = names.filter((r) => selected.has(r.name));
  const movingAllot = selRows.reduce((a, r) => a + r.allot, 0);
  const movingSm = selRows.reduce((a, r) => a + r.sm, 0);
  // What stays on source — data-bearing names not selected (includes SM-only names
  // like a bare short-code, which the backend's allotment-only keeps list omits).
  const keptRows = names.filter((r) => !selected.has(r.name) && (r.allot > 0 || r.sm > 0));
  const keptSm = keptRows.reduce((a, r) => a + r.sm, 0);

  // ── target search (assign-to-existing mode) ──
  const runTgtSearch = async (q) => {
    setTgt((s) => ({ ...s, q, selected: null }));
    if (q.trim().length < 2) { setTgt((s) => ({ ...s, results: [], searching: false })); return; }
    setTgt((s) => ({ ...s, searching: true }));
    try {
      const { data } = await adminCollegesLiteSearch(q.trim(), "");
      const rows = (data?.results || data || []).filter((r) => (r.college_code || r.code) !== sourceCode);
      setTgt((s) => ({ ...s, results: rows, searching: false }));
    } catch {
      setTgt((s) => ({ ...s, results: [], searching: false }));
    }
  };

  // Auto-suggest the new master's state from the SELECTED names. Backend tags each
  // name with a `state` + `state_conf`: "data" (stamped by a state counselling body)
  // and "name" (the state name is literally in the text) are RELIABLE; "guess" (a
  // city-token match) is not. Rule the admin asked for: if the reliable signal points
  // to exactly ONE state, use it; if the selected names disagree (>1 distinct outcome),
  // leave it BLANK so the admin picks — never guess between two states. Reliable wins
  // over guess; only when there is no reliable signal do guesses decide.
  const RELIABLE = new Set(["data", "name"]);
  const suggestState = () => {
    const reliable = [...new Set(selRows.filter((r) => RELIABLE.has(r.state_conf) && r.state).map((r) => r.state))];
    if (reliable.length === 1) return reliable[0];
    if (reliable.length > 1) return "";                   // conflicting reliable signals → blank
    const guesses = [...new Set(selRows.filter((r) => r.state).map((r) => r.state))];
    return guesses.length === 1 ? guesses[0] : "";        // single guess → use; none/multi → blank
  };

  // ── step transitions ──
  const goStep2 = () => {
    // prefill identity from chosen new-primary (or the busiest selected name)
    const pn = newPrimary || (selRows.slice().sort((a, b) => (b.allot + b.sm) - (a.allot + a.sm))[0]?.name) || "";
    setNewPrimary(pn);
    setIdent((s) => ({ ...s, primary_name: pn, short_name: s.short_name || pn, state: suggestState() }));
    setMoveNmc(!!nmcName && selected.has(nmcName));
    if (!newCode) setNewCode(suggestCode(pn, sourceCode));
    setError(""); setStep(2);
  };

  const goStep3 = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await splitMaster({ ...buildPayload(), dry_run: true });
      setPreview(data);
      // Backend is authoritative: it force-moves nmc_data when a picked name matches the
      // NMC identity (normalized), even if the step-2 exact-match pre-check missed a variant.
      if (data?.nmc_auto) setMoveNmc(true);
      // if source's current primary/short was lifted, default a replacement
      const srcPrimary = bd?.master?.primary_name || "";
      const lifted = selected.has(srcPrimary) || selected.has(bd?.master?.short_name);
      if (lifted) setSourcePrimary((data?.source_keeps?.names || [])[0]?.name || "");
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const movableSet = new Set(movableCodes.map(([b]) => b));  // never move an identity code
    const base = {
      source_code: sourceCode,
      move_names: Array.from(selected),
      move_codes: Object.entries(codeMoves).filter(([b, v]) => v && movableSet.has(b)).map(([b]) => b),
      move_nmc: moveNmc,
      source_primary: sourcePrimary || undefined,
    };
    if (dest === "existing") {
      return { ...base, target_code: tgt.selected?.college_code || tgt.selected?.code };
    }
    return {
      ...base,
      new_code: newCode.trim(),
      new_master: {
        primary_name: ident.primary_name.trim(),
        short_name: (ident.short_name || ident.primary_name).trim(),
        state: ident.state.trim(),
        college_type: ident.college_type,
      },
    };
  };

  const step2Ready = dest === "existing"
    ? !!(tgt.selected?.college_code || tgt.selected?.code)
    : !!(newCode.trim() && ident.primary_name.trim());

  const apply = async () => {
    setApplying(true); setError("");
    try {
      const { data } = await splitMaster({ ...buildPayload(), dry_run: false });
      setResult(data);
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Split failed");
    } finally {
      setApplying(false);
    }
  };

  const liftedSourcePrimary = bd && (selected.has(bd?.master?.primary_name) || selected.has(bd?.master?.short_name));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="split-master-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowsSplit size={16} weight="bold" className="text-[#6043EF]" />
            Split this master into a new college
            <span className="ml-1 text-[11px] font-normal text-zinc-400">Step {step} of 3 · {sourceCode}</span>
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="text-xs text-[#B91C1C] bg-red-50 border border-red-200 rounded p-2 flex gap-2" data-testid="split-error">
            <Warning size={14} className="shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {loading && !bd && (
          <div className="py-10 flex items-center justify-center text-zinc-500 text-sm gap-2">
            <SpinnerGap size={18} className="animate-spin" /> Loading…
          </div>
        )}

        {result ? (
          <div className="py-6" data-testid="split-result">
            <div className="text-sm text-[#15803D] bg-green-50 border border-green-200 rounded p-3 flex gap-2">
              <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">
                  Split complete — {result.mode === "existing" ? "assigned into" : "new master"} {result.dest_code}
                </div>
                <div className="text-xs mt-1 text-zinc-600">
                  Moved {result.moved?.raw_allotments ?? 0} allotment + {result.moved?.seat_matrix_detail ?? 0} SM rows ·
                  aliases: {(result.dest_master?.aliases_added || []).length} · codes: {Object.keys(result.dest_master?.codes_added || {}).join(", ") || "—"}
                  {result.dest_master?.nmc_moved ? " · nmc_data moved" : ""}
                  {result.code_conflicts?.length ? ` · ${result.code_conflicts.length} code conflict(s) skipped` : ""}
                </div>
                <div className="text-xs mt-1 text-zinc-500">
                  Registry: {result.registry?.claimed ?? 0} claimed / {result.registry?.released ?? 0} released
                  {result.registry?.skipped?.length ? ` · ${result.registry.skipped.length} skipped (collision)` : ""}
                </div>
              </div>
            </div>

            {/* background-job progress: derived rebuild (always) + enrichment (new master) */}
            {result.rebuild_job && (
              <JobProgress data-testid="split-rebuild"
                job={jobProg.rebuild}
                title="Rebuilding cutoffs / seats / search"
                doneText="Cutoffs, seats & search rebuilt."
                failText="Rebuild failed — re-run cutoffs for these colleges from the admin console." />
            )}
            {result.enrich_job && (
              <JobProgress data-testid="split-enrich"
                job={jobProg.enrich}
                title="Enriching new master"
                subtitle="Places → Website → Details → Media"
                doneText="Enrichment complete — Places, website, details & media filled."
                failText="Enrichment failed — re-run it from the /colleges “Fill New” panel." />
            )}

            <div className="mt-3 text-[10px] text-zinc-400">These finish in the background — you can safely close this dialog.</div>

            <DialogFooter className="mt-4">
              <Button onClick={onClose} data-testid="split-done" className="bg-[#6043EF] hover:bg-[#4d35c9] text-white">Done</Button>
            </DialogFooter>
          </div>
        ) : bd && (
          <>
            {/* ───────────── STEP 1: NAMES ───────────── */}
            {step === 1 && (
              <div data-testid="split-step-1">
                <div className="text-[11px] text-zinc-500 mb-2">
                  Master: <b>{bd.master.primary_name}</b> · {bd.master.state || "—"} · codes {Object.keys(bd.master.codes || {}).join(", ") || "—"}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative flex-1">
                    <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter names (e.g. mahab)…"
                      className="h-8 pl-7 text-xs" data-testid="split-filter" />
                  </div>
                  <Button size="sm" variant="outline" onClick={selectAllFiltered} className="h-8 text-[11px]" data-testid="split-select-filtered">
                    Select {filter.trim() ? "matching" : "all"} ({filtered.length})
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearAll} className="h-8 text-[11px] text-zinc-500" data-testid="split-clear">Clear</Button>
                </div>

                <div className="max-h-[42vh] overflow-y-auto border border-zinc-200 rounded divide-y divide-zinc-100" data-testid="split-names">
                  {filtered.map((r) => (
                    <label key={r.name} className="flex items-start gap-2 px-2 py-1.5 hover:bg-zinc-50 cursor-pointer text-xs" data-testid={`split-name-${r.cluster}`}>
                      <Checkbox checked={selected.has(r.name)} onCheckedChange={() => toggle(r.name)} className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-sans text-[#09090B] break-words">{r.name}</div>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          {(r.allot + r.sm) > 0 ? (
                            <span className="text-[10px] text-zinc-500 tabular-nums" title="Allotment · Seat-matrix rows (results are re-derived from allotments on split)">A{r.allot} · S{r.sm}</span>
                          ) : (
                            <span className="text-[10px] italic text-zinc-400" title="No data rows — this is only a name on the master">{metaLabel(r.sources)}</span>
                          )}
                          {r.state && (
                            <Badge variant="outline"
                              className={`text-[8px] py-0 ${RELIABLE.has(r.state_conf) ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-amber-300 text-amber-700 bg-amber-50"}`}
                              title={r.state_conf === "data" ? "From the counselling body's own state field (reliable)"
                                : r.state_conf === "name" ? "The state name appears in the college text (reliable)"
                                : "Guessed from a city/district in the name"}>
                              {r.state}{r.state_conf === "guess" ? " ?" : ""}
                            </Badge>
                          )}
                          {!r.state && cityHint(r.name) && (
                            <Badge variant="outline" className="text-[8px] py-0 border-sky-300 text-sky-700 bg-sky-50"
                              title="Location from the name (no state could be inferred) — check this to tell near-identical names apart">
                              📍 {cityHint(r.name)}
                            </Badge>
                          )}
                          {r.bodies.map((b) => <Badge key={b} variant="outline" className="text-[8px] py-0 border-zinc-300 text-zinc-500">{b}</Badge>)}
                          {r.name === nmcName && <Badge className="text-[8px] py-0 bg-[#6043EF] text-white">NMC</Badge>}
                          <button type="button" onClick={(e) => { e.preventDefault(); selectCluster(r.cluster); }}
                            className="text-[9px] text-[#6043EF] hover:underline" title="Select all same-name variants">+cluster</button>
                        </div>
                      </div>
                    </label>
                  ))}
                  {filtered.length === 0 && <div className="p-3 text-center text-zinc-400 text-xs">No names match “{filter}”.</div>}
                </div>

                <div className="mt-2 text-[11px] text-zinc-600" data-testid="split-selcount">
                  <b>{selCount}</b> selected → moving <b>{movingAllot}</b> allot + <b>{movingSm}</b> SM rows out of {sourceCode}.
                </div>

                {selCount > 0 && (
                  <div className="mt-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">
                      New master's primary name <span className="font-normal normal-case text-zinc-400">— only if you create a new master (ignored when assigning to an existing one; defaults to the busiest name)</span>
                    </div>
                    <div className="max-h-24 overflow-y-auto flex flex-col gap-0.5">
                      {selRows.map((r) => (
                        <label key={r.name} className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                          <input type="radio" name="newprimary" checked={newPrimary === r.name} onChange={() => setNewPrimary(r.name)} />
                          <span className="break-words">{r.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={onClose} data-testid="split-cancel-1">Cancel</Button>
                  <Button onClick={goStep2} disabled={selCount === 0} className="bg-[#6043EF] hover:bg-[#4d35c9] text-white" data-testid="split-next-1">Next: codes →</Button>
                </DialogFooter>
              </div>
            )}

            {/* ───────────── STEP 2: CODES + IDENTITY ───────────── */}
            {step === 2 && (
              <div data-testid="split-step-2" className="space-y-3">
                <div className="flex items-center gap-4 text-[12px]" data-testid="split-dest-toggle">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Destination</span>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="dest" checked={dest === "new"} onChange={() => setDest("new")} /> Create new master
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="dest" checked={dest === "existing"} onChange={() => setDest("existing")} /> Assign to existing master
                  </label>
                </div>

                {dest === "existing" && (
                  <div data-testid="split-target">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Target master</div>
                    {tgt.selected ? (
                      <div className="flex items-center gap-2 border border-[#6043EF]/40 bg-indigo-50 rounded p-2 text-xs" data-testid="split-target-selected">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{tgt.selected.short_name || tgt.selected.primary_name}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">{tgt.selected.college_code || tgt.selected.code} · {tgt.selected.state}</div>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setTgt((s) => ({ ...s, selected: null }))}>change</Button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                          <Input value={tgt.q} onChange={(e) => runTgtSearch(e.target.value)} placeholder="Search the correct master to fold into…"
                            className="h-8 pl-7 text-xs" data-testid="split-target-search" />
                        </div>
                        <div className="mt-1 max-h-40 overflow-y-auto border border-zinc-200 rounded divide-y divide-zinc-100">
                          {tgt.searching && <div className="p-2 text-xs text-zinc-500">Searching…</div>}
                          {!tgt.searching && tgt.q.trim().length >= 2 && tgt.results.length === 0 && <div className="p-2 text-xs text-zinc-500">No matches.</div>}
                          {tgt.results.map((r) => {
                            const rc = r.college_code || r.code;
                            return (
                              <button key={rc} type="button" onClick={() => setTgt((s) => ({ ...s, selected: r }))}
                                className="w-full text-left px-2 py-1.5 hover:bg-zinc-50 text-xs" data-testid={`split-target-result-${rc}`}>
                                <div className="font-semibold truncate">{r.short_name || r.primary_name}</div>
                                <div className="text-[10px] text-zinc-500 font-mono">{rc} · {r.state}{r.city ? ` · ${r.city}` : ""}</div>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {dest === "new" && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">New master identity</div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[11px]">Internal code
                      <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} className="h-8 text-xs mt-0.5" data-testid="split-newcode" />
                    </label>
                    <label className="text-[11px]">State
                      <select value={ident.state} onChange={(e) => setIdent((s) => ({ ...s, state: e.target.value }))}
                        className="h-8 text-xs mt-0.5 w-full border border-zinc-300 rounded px-2 bg-white" data-testid="split-state">
                        <option value="">— select state —</option>
                        {(bd.states || []).map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </label>
                    <label className="text-[11px] col-span-2">Primary name
                      <Input value={ident.primary_name} onChange={(e) => setIdent((s) => ({ ...s, primary_name: e.target.value }))} className="h-8 text-xs mt-0.5" data-testid="split-primary" />
                    </label>
                    <label className="text-[11px] col-span-2">Short name
                      <Input value={ident.short_name} onChange={(e) => setIdent((s) => ({ ...s, short_name: e.target.value }))} className="h-8 text-xs mt-0.5" data-testid="split-short" />
                    </label>
                    <label className="text-[11px]">Type
                      <select value={ident.college_type} onChange={(e) => setIdent((s) => ({ ...s, college_type: e.target.value }))}
                        className="h-8 text-xs mt-0.5 w-full border border-zinc-300 rounded px-2" data-testid="split-type">
                        <option value="govt">govt</option><option value="private">private</option><option value="deemed">deemed</option>
                      </select>
                    </label>
                  </div>
                </div>
                )}

                {nmcName && (
                  <label className="flex items-center gap-2 text-[11px]" data-testid="split-move-nmc">
                    <Checkbox checked={moveNmc} onCheckedChange={(v) => setMoveNmc(!!v)} />
                    Move <b>nmc_data</b>: {nmcName}{bd.master.nmc_state ? ` · ${bd.master.nmc_state}` : ""} — to the {dest === "existing" ? "target" : "new"} master
                  </label>
                )}

                {/* Codes section only when there is a genuinely-movable (non-identity) code. */}
                {movableCodes.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Codes on the source — select if any to be moved to target</div>
                    {movableCodes.map(([b, v]) => (
                      <label key={b} className="flex items-center gap-2 text-[11px] py-0.5" data-testid={`split-code-${b}`}>
                        <Checkbox checked={!!codeMoves[b]} onCheckedChange={(val) => setCodeMoves((s) => ({ ...s, [b]: !!val }))} />
                        <span className="font-mono">{b} → {v}</span>
                        <span className="text-zinc-400">{codeMoves[b] ? (dest === "existing" ? "move to target" : "move to new") : "stays on source"}</span>
                      </label>
                    ))}
                    <div className="text-[10px] text-zinc-400 mt-1">Need more codes on the destination? Add them from the college's CODES panel after the split.</div>
                  </div>
                )}

                <DialogFooter className="mt-2">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="split-back-2"><ArrowLeft size={13} className="mr-1" /> Back</Button>
                  <Button onClick={goStep3} disabled={loading || !step2Ready} className="bg-[#6043EF] hover:bg-[#4d35c9] text-white" data-testid="split-next-2">
                    {loading ? <SpinnerGap size={14} className="animate-spin mr-1" /> : null} Next: preview →
                  </Button>
                </DialogFooter>
              </div>
            )}

            {/* ───────────── STEP 3: PREVIEW + CONFIRM ───────────── */}
            {step === 3 && preview && (
              <div data-testid="split-step-3" className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {/* ── destination card ── */}
                  <div className="rounded-xl border border-[#6043EF]/25 bg-[#6043EF]/[0.04] p-3.5 flex flex-col">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#6043EF]">
                      <ArrowsSplit size={13} weight="bold" />
                      {dest === "existing" ? `Existing master ${preview.target?.college_code}` : `New master ${newCode}`}
                    </div>
                    <div className="text-[15px] font-semibold text-zinc-900 mt-2 leading-snug">
                      {dest === "existing" ? preview.target?.primary_name : ident.primary_name}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {dest === "existing" ? preview.target?.state : `${ident.state} · ${ident.college_type}`}
                    </div>
                    <div className="text-sm text-zinc-700 mt-2.5">
                      Moving <b className="text-lg text-[#6043EF]">{preview.move?.raw_allotments}</b> allot
                      {" + "}<b className="text-lg text-[#6043EF]">{preview.move?.seat_matrix_detail}</b> SM
                    </div>
                    <div className="border-t border-[#6043EF]/15 my-2.5" />
                    <div className="max-h-40 overflow-y-auto text-xs leading-relaxed space-y-0.5">
                      {selRows.map((r) => {
                        const parts = [];
                        if (r.allot > 0) parts.push(`${r.allot}×A`);
                        if (r.sm > 0) parts.push(`${r.sm}×S`);
                        return (
                          <div key={r.name} className="truncate" title={r.name}>
                            {parts.length ? (
                              <span className="font-mono font-bold text-zinc-900">{parts.join(" · ")}</span>
                            ) : (
                              <span className="italic text-zinc-400">{metaLabel(r.sources)}</span>
                            )}{" "}
                            <span className="text-zinc-600">{r.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── source keeps card ── */}
                  <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3.5 flex flex-col">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Source {sourceCode} keeps</div>
                    <div className="text-sm text-zinc-700 mt-2">
                      <b className="text-lg text-zinc-900">{keptRows.length}</b> names
                      {" · "}<b className="text-lg text-zinc-900">{keptSm}</b> SM
                    </div>
                    <div className="border-t border-zinc-200 my-2.5" />
                    <div className="max-h-40 overflow-y-auto text-xs leading-relaxed space-y-0.5">
                      {keptRows.map((r) => {
                        const parts = [];
                        if (r.allot > 0) parts.push(`${r.allot}×A`);
                        if (r.sm > 0) parts.push(`${r.sm}×S`);
                        return (
                          <div key={r.name} className="truncate" title={r.name}>
                            <span className="font-mono font-bold text-zinc-900">{parts.join(" · ")}</span>{" "}
                            <span className="text-zinc-600">{r.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {preview.warning && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded p-2 flex gap-2">
                    <Warning size={13} className="shrink-0 mt-0.5" /> {preview.warning}
                  </div>
                )}

                {(preview.nmc_conflicts || []).length > 0 && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded p-2" data-testid="split-nmc-conflicts">
                    <div className="flex gap-2 font-semibold"><Warning size={13} className="shrink-0 mt-0.5" /> nmc_data seat overlaps — target kept, source discarded:</div>
                    <ul className="mt-1 ml-5 list-disc">
                      {preview.nmc_conflicts.map((c, i) => (
                        <li key={i}>{c.year} {c.course}: target keeps <b>{c.target}</b> (source {c.source} discarded)</li>
                      ))}
                    </ul>
                  </div>
                )}

                {liftedSourcePrimary && (
                  <div data-testid="split-source-primary">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Source primary was moved — set a new one</div>
                    <select value={sourcePrimary} onChange={(e) => setSourcePrimary(e.target.value)}
                      className="h-8 text-xs w-full border border-zinc-300 rounded px-2" data-testid="split-source-primary-select">
                      {!sourcePrimary && <option value="">— select a replacement —</option>}
                      {(preview.source_keeps?.names || []).map((n) => <option key={n.name} value={n.name}>{n.name}</option>)}
                    </select>
                    <div className="text-[10px] text-zinc-400 mt-1">Required — the source's own primary name is moving out, so pick one of its remaining names.</div>
                  </div>
                )}

                <div className="text-[11px] text-zinc-600 space-y-0.5">
                  <div>
                    Codes moving: <b className="text-zinc-900">
                      {(preview.codes_plan?.move || []).length
                        ? (preview.codes_plan.move).join(", ")
                        : "none"}
                    </b>
                    <span className="text-zinc-400"> (auto-derived from the moved data's bodies — national codes travel, state codes only to a same-state target)</span>
                  </div>
                  {(preview.codes_plan?.dropped || []).length > 0 && (
                    <div className="text-amber-700">
                      Dropped (state code invalid on target): <b>
                        {preview.codes_plan.dropped.map((d) => `${d.body}→${d.value} (${d.state}≠${d.dest_state})`).join(", ")}
                      </b>
                    </div>
                  )}
                  {(preview.codes_plan?.overrides || []).length > 0 && (
                    <div className="text-emerald-700">
                      Overriding stale code on target: <b>
                        {preview.codes_plan.overrides.map((o) => `${o.body} ${o.old} → ${o.new}`).join(", ")}
                      </b>
                      <span className="text-zinc-400"> (a real registration code replaces a dangling NMC_* pointer)</span>
                    </div>
                  )}
                  {(preview.codes_plan?.conflicts || []).length > 0 && (
                    <div className="text-red-700">
                      Code conflict (left on target — not overwritten): <b>
                        {preview.codes_plan.conflicts.map((c) => `${c.body}: target ${c.target_has} vs incoming ${c.incoming}`).join(", ")}
                      </b>
                    </div>
                  )}
                  {nmcName && (
                    <div>
                      NMC seat data <span className="text-zinc-400">(nmc_data — per-year MBBS/course intake)</span>:{" "}
                      {moveNmc
                        ? <b className="text-zinc-900">
                            {preview.nmc_override
                              ? `replaces the target's stale nmc_data (source wins)`
                              : `moves to the ${dest === "existing" ? "target" : "new"} master`}
                            {preview.nmc_auto && <span className="ml-1 font-normal text-[#6043EF]">(auto — a moved name is the NMC identity)</span>}
                          </b>
                        : <b className="text-zinc-900">stays on source</b>}
                    </div>
                  )}
                </div>

                <div className="text-[10px] text-zinc-400">No undo — rows are re-pointed (recoverable via re-split/merge), aliases/codes are re-addable.</div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setStep(2)} disabled={applying} data-testid="split-back-3"><ArrowLeft size={13} className="mr-1" /> Back</Button>
                  <Button onClick={apply} disabled={applying || (liftedSourcePrimary && !sourcePrimary)} className="bg-[#6043EF] hover:bg-[#4d35c9] text-white" data-testid="split-apply">
                    {applying ? <SpinnerGap size={14} className="animate-spin mr-1" /> : null}
                    {applying ? "Splitting…" : "Confirm split"}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Compact progress row for a background job polled via jobsStatus.
function JobProgress({ job, title, subtitle, doneText, failText, "data-testid": testId }) {
  const status = job?.status;
  const p = job?.progress;
  return (
    <div className="mt-3 border border-zinc-200 rounded p-3" data-testid={testId}>
      {status === "completed" ? (
        <div className="text-xs text-[#15803D] flex items-center gap-2">
          <CheckCircle size={14} weight="fill" /> {doneText}
        </div>
      ) : status === "failed" || status === "cancelled" ? (
        <div className="text-xs text-amber-700 flex items-center gap-2">
          <Warning size={14} /> {failText}
        </div>
      ) : (
        <>
          <div className="text-xs text-zinc-600 flex items-center gap-2">
            <SpinnerGap size={14} className="animate-spin text-[#6043EF]" />
            {title}{p?.stage ? `: ${p.stage}` : "…"}
            {p?.step_index ? ` (${p.step_index}/${p.step_total || "?"})` : ""}
          </div>
          <div className="mt-1.5 h-1 w-full bg-zinc-100 rounded overflow-hidden">
            <div className="h-full bg-[#6043EF] transition-all" style={{ width: `${p?.current ?? 5}%` }} />
          </div>
          {subtitle && <div className="mt-1 text-[10px] text-zinc-400">{subtitle}</div>}
        </>
      )}
    </div>
  );
}

// Location cue from a verbatim college name: the trailing comma-segment (usually the
// city/place, e.g. "…Hospital, Solan" → "Solan", "…Research, Sadopur" → "Sadopur").
// Used to tell near-identical metadata aliases apart when no state chip is inferable.
function cityHint(name = "") {
  const seg = String(name).split(",").pop().trim();
  if (seg.length < 2 || seg.length > 30) return null;
  if (!/[a-z]/i.test(seg) || seg.includes("&")) return null;
  if (/\b(college|hospital|institute|university|sciences|research|medical)\b/i.test(seg)) return null;
  return seg;
}

// A name with zero A/S/R rows is metadata only — say what it is (alias / short /
// primary / nmc) instead of the misleading "A0 · S0 · R0".
function metaLabel(sources = []) {
  const kinds = new Set(sources.map((x) => String(x).split(":")[0]));
  if (kinds.has("primary")) return "primary name";
  if (kinds.has("short")) return "short name";
  if (kinds.has("nmc")) return "nmc name";
  if (kinds.has("alias")) return "alias";
  return "name only";
}

function suggestCode(primaryName, sourceCode) {
  const src = (sourceCode || "").toUpperCase();
  const words = (primaryName || "").replace(/[^a-zA-Z ]/g, " ").split(/\s+/).filter(Boolean);
  const initials = words.map((w) => w[0]).join("").toUpperCase().slice(0, 6);
  let base = initials.length >= 3 ? initials : `${src}2`;
  // Never suggest the source's own code (initials can coincide with it, e.g.
  // "Govt Medical College Mahabubnagar" → GMCM == source GMCM). Disambiguate with
  // the longest word (usually the city). Server still enforces registry uniqueness.
  if (base.toUpperCase() === src) {
    const longest = words.slice().sort((a, b) => b.length - a.length)[0] || "";
    base = longest ? `${initials}_${longest.slice(0, 3).toUpperCase()}` : `${initials}2`;
  }
  return base;
}
